import { initializeApp } from 'firebase/app';
import {
  getAuth,
  browserLocalPersistence,
  initializeAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  connectAuthEmulator,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { isEmulatorEnv } from '../utils/apiBase';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

// ============================================================================
//  iOS FIX: IndexedDB doesn't work properly in WKWebView

// Use browserLocalPersistence on native platforms instead
// ============================================================================
let auth: ReturnType<typeof getAuth>;
if (Capacitor.isNativePlatform()) {
  auth = initializeAuth(app, {
    persistence: browserLocalPersistence
  });
} else {
  auth = getAuth(app);
}

// ============================================================================
//  GOOGLE SIGN-IN

// Uses @capgo/capacitor-social-login on iOS/Android (has privacy manifests)
// Uses popup on web
// ============================================================================
export const signInWithGoogleNative = async () => {
  if (Capacitor.isNativePlatform()) {
    // Native: Use @capgo/capacitor-social-login plugin
    const { SocialLogin } = await import('@capgo/capacitor-social-login');
    
    // Initialize the plugin (only needs to be done once, but safe to call multiple times)
    // Web Client ID: Get from Google Cloud Console > APIs & Services > Credentials > OAuth 2.0 Client IDs > Web client
    // iOS Client ID: From GoogleService-Info.plist CLIENT_ID field
    await SocialLogin.initialize({
      google: {
        webClientId: process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || '',
        iOSClientId: '92943320710-ae8kl8m3aed1ksmjemckklusihdprikq.apps.googleusercontent.com',
      }
    });
    
    // Sign in with Google using native SDK
    const result = await SocialLogin.login({
      provider: 'google',
      options: {
        scopes: ['profile', 'email']
      }
    });
    
    if (result.result?.idToken) {
      // Get the ID token to sign into Firebase JS SDK
      const credential = GoogleAuthProvider.credential(result.result.idToken);
      
      // Sign in to Firebase with the credential
      return signInWithCredential(auth, credential);
    } else {
      throw new Error('No ID token received from Google Sign-In');
    }
  } else {
    // Web: Use popup
    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');
    return signInWithPopup(auth, provider);
  }
};

// Nonce helpers for Sign in with Apple (see signInWithAppleNative)
const randomNonce = (length = 32): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
};
const sha256Hex = async (input: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
};

// ============================================================================
//  APPLE SIGN-IN

// Uses @capgo/capacitor-social-login on iOS (native Sign in with Apple)
// Uses OAuthProvider popup on web
// ============================================================================
export const signInWithAppleNative = async () => {
  if (Capacitor.isNativePlatform()) {
    // Native: Use @capgo/capacitor-social-login plugin
    const { SocialLogin } = await import('@capgo/capacitor-social-login');

    // On iOS the plugin talks to Apple natively and hands back the identity
    // token directly. A redirectUrl must NOT be set there: with one present
    // the plugin POSTs the auth code to that URL and expects a 3xx back, and
    // Firebase's auth handler answers 200, which surfaced to players as
    // "Invalid response code: 200." The redirect flow is Android-only.
    const isAndroid = Capacitor.getPlatform() === 'android';
    await SocialLogin.initialize({
      apple: isAndroid
        ? {
            clientId: 'com.pokersweepstars.app',
            redirectUrl: 'https://pokersweepstars.firebaseapp.com/__/auth/handler',
          }
        : {},
    });

    // Apple wants the SHA-256 of the nonce in the request; Firebase wants the
    // raw nonce back to verify the token was minted for this sign-in.
    const rawNonce = randomNonce();
    const hashedNonce = await sha256Hex(rawNonce);

    const result = await SocialLogin.login({
      provider: 'apple',
      options: {
        scopes: ['email', 'name'],
        nonce: hashedNonce,
      },
    });

    // This plugin version labels the fields oddly on iOS: the JWT identity
    // token arrives in accessToken.token and idToken carries the one-time
    // authorization code. Take whichever is the actual JWT.
    const looksLikeJwt = (v: unknown): v is string => typeof v === 'string' && v.split('.').length === 3;
    const r: any = result.result || {};
    const identityToken = looksLikeJwt(r.accessToken?.token)
      ? r.accessToken.token
      : looksLikeJwt(r.idToken) ? r.idToken : null;

    if (!identityToken) {
      throw new Error('No identity token received from Apple Sign-In');
    }

    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({
      idToken: identityToken,
      rawNonce,
    });
    return signInWithCredential(auth, credential);
  } else {
    // Web: Use OAuthProvider popup
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return signInWithPopup(auth, provider);
  }
};

export { auth };
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

//  EMULATOR MODE - this module previously NEVER connected to emulators,

// so any page importing from services/firebase talked to production even
// during local testing. Same detection + double-connect guard as
// src/firebase.ts (both modules share the [DEFAULT] app instance).
if (isEmulatorEnv() && !(globalThis as any).__pssEmulatorsConnected) {
  (globalThis as any).__pssEmulatorsConnected = true;
  try {
    console.log('🧪 [EMULATOR] Connecting Firebase SDK to local emulators');
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    connectFunctionsEmulator(functions, 'localhost', 5001);
  } catch (e) {
    console.warn('🧪 [EMULATOR] SDK connect skipped (already connected?):', e);
  }
}